import { LinkText } from '@/components';
import { LogoutModal } from '@/features/authentication/_components/LogoutModal';
import { useMe } from '@/features/authentication/_hooks/me';
import { SearchUsersModal } from '@/features/tenant-manager/user/_components/search-modal';
import { ITEM_SPLITTER, isRS, logger } from '@/utils';
import {
  GlobalOutlined,
  LogoutOutlined,
  ReadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { type FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import {
  type TopMenuItem,
  getSelectedMenuKeyByPathname,
} from '../_utils/side-menu';
import { ServiceLogo } from './ServiceLogo';
import { SIDE_MENU } from './SideMenu';
import { SidebarContent } from './SidebarContent';

type Props = {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
};

export const PageSidebar: FC<Props> = ({ collapsed, setCollapsed }) => {
  const { t } = useTranslation();
  const me = useMe();
  const location = useLocation();
  const [openLogoutModal, setOpenLogoutModal] = useState(false);
  const [
    openUsersOrOrganizationsSearchModal,
    setOpenUsersOrOrganizationsSearchModal,
  ] = useState(false);

  const selectedKey = getSelectedMenuKeyByPathname({
    pathname: location.pathname,
  });

  const isNotJoinOrganization = !me.data?.organizationIds.length;

  const sideMenu = SIDE_MENU();
  const topMenu = useMemo<TopMenuItem[]>(() => {
    return sideMenu.filter(({ key }) => {
      switch (true) {
        case key === 'analysis':
        case key === 'project': {
          return (
            me.data?.role.resourceGroupPermissions.project === 'view' ||
            me.data?.role.resourceGroupPermissions.project === 'edit' ||
            me.data?.role.resourceGroupPermissions.project ===
              'view-all-privilege' ||
            me.data?.role.resourceGroupPermissions.project === 'privilege'
          );
        }
        case key === 'globalTag': {
          return (
            me.data?.role.resourceGroupPermissions.globalTag === 'view' ||
            me.data?.role.resourceGroupPermissions.globalTag === 'edit'
          );
        }
        case key === 'localTag': {
          return (
            me.data?.role.resourceGroupPermissions.localTag === 'view' ||
            me.data?.role.resourceGroupPermissions.localTag === 'edit'
          );
        }
        case key === 'masterTable': {
          return (
            me.data?.role.resourceGroupPermissions.masterTable === 'view' ||
            me.data?.role.resourceGroupPermissions.masterTable === 'edit'
          );
        }
        case key === 'tenantManager': {
          return (
            me.data?.role.resourceGroupPermissions.administrator === 'view' ||
            me.data?.role.resourceGroupPermissions.administrator === 'edit'
          );
        }
        case key === 'comment':
        case key === 'workflow':
        case key === 'batch': {
          return !isNotJoinOrganization;
        }
        default: {
          const check: never = key;
          logger.error('unknown menu key', check);
          return false;
        }
      }
    });
  }, [
    me.data?.role.resourceGroupPermissions.administrator,
    me.data?.role.resourceGroupPermissions.globalTag,
    me.data?.role.resourceGroupPermissions.localTag,
    me.data?.role.resourceGroupPermissions.masterTable,
    me.data?.role.resourceGroupPermissions.project,
    sideMenu,
    isNotJoinOrganization,
  ]);

  const bottomMenu = useMemo(
    () =>
      [
        {
          label: (
            <LinkText
              text={t('document-link')}
              color="normal"
              href="/docs/user-manual.pdf"
            />
          ),
          key: 'document-link',
          icon: <ReadOutlined aria-label="" />,
          hidden: !isRS(),
        },
        {
          label: (
            <LinkText
              text={t('contact-us-link')}
              color="normal"
              href="https://www.gyoukaku.go.jp/review/review.html"
            />
          ),
          key: 'contact-us-link',
          icon: <GlobalOutlined aria-label="" />,
          hidden: !isRS(),
        },
        {
          key: 'users-organizations-search',
          label: t('search-users-or-organizations'),
          icon: <SearchOutlined aria-label="" />,
          onClick: () => {
            setOpenUsersOrOrganizationsSearchModal(true);
          },
          hidden: isRS(),
        },
        {
          label: <LinkText text={t('note.note')} color="normal" href="/note" />,
          key: 'note',
          icon: <ReadOutlined />,
          hidden: isRS(),
        },
        {
          label: t('logout'),
          key: 'logout',
          icon: <LogoutOutlined aria-label="" />,
          onClick: () => {
            setOpenLogoutModal(true);
          },
        },
      ].filter((item) => !item.hidden),
    [t],
  );

  return (
    <>
      <SidebarContent
        collapsed={collapsed}
        onCollapse={(value) => {
          setCollapsed(value);
        }}
        accountName={me.data?.name ?? ''}
        organizationName={me.data?.organizations
          .map((d) => d.name)
          .join(ITEM_SPLITTER)}
        iconUrl={me.data?.iconDataUrl}
        selectedKey={selectedKey}
        logo={<ServiceLogo collapsed={collapsed} />}
        topMenu={topMenu}
        bottomMenu={bottomMenu}
        secondLastLoginTime={me.data?.secondLastLoginTime}
      />
      <LogoutModal
        open={openLogoutModal}
        onClose={() => {
          setOpenLogoutModal(false);
        }}
      />
      <SearchUsersModal
        open={openUsersOrOrganizationsSearchModal}
        onClose={() => {
          setOpenUsersOrOrganizationsSearchModal(false);
        }}
      />
    </>
  );
};
